import { SENTRY_OPTIONS } from '@Constants/sentryConstants';
import { useSession } from '@Contexts/session';
import { useMandantEntitlements, useOwnUser } from '@Hooks/Permissions';
import { useRemoteCacheInvalidation } from '@Hooks/useRemoteCacheInvalidation';
import { SentryTags } from '@Model';
import * as Sentry from '@sentry/react';
import { LoadingFrame } from '@synqo/frontend-component-library';
import { type PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { type AppState, AppStateContext, type AppStatePayload, emptyProfile } from './AppStateContext';

export function AppStateProvider({ children }: PropsWithChildren) {
    const { current: session } = useSession();
    const { profile, isLoading: isProfileLoading, refetch: refetchProfile } = useOwnUser(session.partnershipId);
    const { entitlements, refetchEntitlements } = useMandantEntitlements(session.partnershipId);

    const [currentState, setCurrentState] = useState<AppState>({});

    useRemoteCacheInvalidation('profile', refetchProfile);

    useEffect(() => {
        if (!SENTRY_OPTIONS.enabled || !profile) {
            return;
        }

        Sentry.setUser({
            id: profile.id,
            email: profile.email,
            username: profile.name,
        });

        Sentry.setTags({
            [SentryTags.client_id]: session.clientId,
            [SentryTags.mandant_id]: session.mandantId,
            [SentryTags.partnership_id]: session.partnershipId,
        });
    }, [profile, session]);

    const updateAppState = useCallback(
        (key: string, value: string) =>
            setCurrentState((current) => ({
                ...current,
                [key]: value,
            })),
        [setCurrentState],
    );

    const value = useMemo<AppStatePayload>(
        () => ({
            profile: profile ?? emptyProfile,
            entitlements: entitlements ?? {},
            reload: () => {
                refetchProfile();
                refetchEntitlements();
            },

            current: currentState,
            set: updateAppState,
        }),
        [profile, entitlements, currentState, refetchProfile, refetchEntitlements, updateAppState],
    );

    if (isProfileLoading) {
        return <LoadingFrame />;
    }

    return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
}
